import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Heart, Play, FileText, ChevronRight, Star, ThumbsUp, ThumbsDown, Sparkles, ChevronLeft, Home, Youtube, Instagram, ExternalLink, MessageCircle, Send, Loader2 } from "lucide-react";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Niamh's cartoon avatar - uploaded by user
const NIAMH_AVATAR = "https://customer-assets-lqy194kg.emergentagent.net/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/l3pyzxtc_Niamh%27s%20Corner-%20WELCOME-KiddiPlay%20App.png";

// Banner image (YouTube/Facebook style) - user provided custom banner
const BANNER_IMAGE = "https://customer-assets-lqy194kg.emergentagent.net/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/pzgjse3s_Banner%20Resized%20Kiddiplay%20Webpage.png";

const CATEGORY_BADGES = {
  loving: { label: "What I'm Loving", color: "bg-pink-100 text-pink-700", emoji: "💖" },
  not_for_me: { label: "Not For Me", color: "bg-slate-100 text-slate-700", emoji: "😅" },
  activity: { label: "Activity", color: "bg-purple-100 text-purple-700", emoji: "🎨" },
  recommendation: { label: "Recommendation", color: "bg-amber-100 text-amber-700", emoji: "⭐" },
  something_new: { label: "Something New!", color: "bg-emerald-100 text-emerald-700", emoji: "✨" },
  liked_activity: { label: "Liked Activity", color: "bg-cyan-100 text-cyan-700", emoji: "🌟" }
};

const NiamhsCorner = ({ isPreview = false }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : "";
      const response = await axios.get(`${API}/niamhs-corner/posts${params}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (postId) => {
    if (!token) {
      toast.error("Please log in to vote!");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/niamhs-corner/posts/${postId}/vote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/niamhs-corner/posts/${postId}/comments`);
      setComments(prev => ({ ...prev, [postId]: response.data.comments || [] }));
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const toggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
    if (!isExpanded && !comments[postId]) {
      await fetchComments(postId);
    }
  };

  const submitComment = async (postId) => {
    if (!token) {
      toast.error("Please log in to comment!");
      return;
    }
    const content = newComment[postId]?.trim();
    if (!content) {
      toast.error("Please write a comment first!");
      return;
    }
    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      await axios.post(
        `${API}/niamhs-corner/posts/${postId}/comments`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Comment submitted! It will appear after moderation 💜");
      setNewComment(prev => ({ ...prev, [postId]: "" }));
    } catch (error) {
      toast.error("Failed to submit comment");
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handlePollVote = async (pollId, optionIndex) => {
    if (!token) {
      toast.error("Please log in to vote!");
      return;
    }
    try {
      await axios.post(
        `${API}/niamhs-corner/polls/${pollId}/vote?option_index=${optionIndex}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Vote recorded! 🎉");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to vote on poll");
    }
  };

  if (loading && !isPreview) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const displayPosts = isPreview ? posts.slice(0, 3) : posts;

  return (
    <div className={isPreview ? "" : "min-h-screen bg-gradient-to-b from-pink-50 to-purple-50 pb-24"}>
      {/* YouTube/Facebook Style Banner - Full Page Only */}
      {!isPreview && (
        <>
          {/* Back Navigation */}
          <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-purple-100">
            <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors">
                <ChevronLeft className="w-5 h-5" />
                <span className="font-medium">Back to Dashboard</span>
              </Link>
              <Link to="/" className="flex items-center gap-2 text-slate-500 hover:text-purple-600 transition-colors">
                <Home className="w-4 h-4" />
                <span className="text-sm">Homepage</span>
              </Link>
            </div>
          </div>

          {/* Banner with centered Niamh picture and colorful background text */}
          <div className="relative w-full overflow-hidden bg-gradient-to-r from-[#e6b6ff] via-[#f8d7ff] to-[#fff7d9] py-6 md:py-10">
            {/* Background Text - NIAMH'S CORNER in playful colors */}
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
              <div className="text-center select-none" style={{ fontFamily: "'Fredoka', 'Comic Sans MS', cursive" }}>
                <div className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold tracking-wide" style={{ opacity: 0.25 }}>
                  <span style={{ color: '#ff6b9d' }}>N</span>
                  <span style={{ color: '#a855f7' }}>I</span>
                  <span style={{ color: '#ff85a2' }}>A</span>
                  <span style={{ color: '#f97316' }}>M</span>
                  <span style={{ color: '#ff6b9d' }}>H</span>
                  <span style={{ color: '#22d3ee' }}>&apos;</span>
                  <span style={{ color: '#a855f7' }}>S</span>
                </div>
                <div className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[10rem] font-bold tracking-wide -mt-4 md:-mt-8" style={{ opacity: 0.25 }}>
                  <span style={{ color: '#a855f7' }}>C</span>
                  <span style={{ color: '#ff6b9d' }}>O</span>
                  <span style={{ color: '#f97316' }}>R</span>
                  <span style={{ color: '#22d3ee' }}>N</span>
                  <span style={{ color: '#ff85a2' }}>E</span>
                  <span style={{ color: '#a855f7' }}>R</span>
                </div>
              </div>
            </div>
            
            {/* Centered Niamh Picture */}
            <div className="relative z-10 flex justify-center">
              <div className="relative">
                <img 
                  src={BANNER_IMAGE}
                  alt="Niamh - Play CEO"
                  className="h-48 sm:h-56 md:h-64 lg:h-72 xl:h-80 object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>

          {/* Channel Info Section - Centered */}
          <div className="max-w-5xl mx-auto px-4 pt-6">
            <div className="flex flex-col items-center gap-4 mb-6 text-center">
              {/* Title and info - centered */}
              <div>
                <p className="text-purple-600 font-semibold">@NiamhPlayCEO</p>
                <p className="text-sm text-gray-600 mt-1">Play • Create • Explore • Smile</p>
                <p className="text-xs text-gray-500 mt-1">124 posts • 2.4K followers • CEO of KiddiPlay Club</p>
              </div>
              
              {/* Social Links & Subscribe - centered */}
              <div className="flex flex-wrap justify-center gap-2">
                <Button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full px-6 hover:shadow-lg transition-all">
                  <Heart className="w-4 h-4 mr-2" />
                  Follow
                </Button>
                <a href="https://www.youtube.com/@KiddiPlayClub" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                    <Youtube className="w-4 h-4 mr-2" />
                    YouTube
                  </Button>
                </a>
                <a href="https://www.instagram.com/kiddiplayclub" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full border-pink-200 text-pink-600 hover:bg-pink-50">
                    <Instagram className="w-4 h-4 mr-2" />
                    Instagram
                  </Button>
                </a>
              </div>
            </div>

            {/* Bio */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-purple-100">
              <p className="text-gray-700 text-center">
                Hey friends! 👋 Welcome to my corner! I&apos;m Niamh, the Play CEO of KiddiPlay Club. 
                Here I share cool stuff I love, activities to try, books I&apos;m reading, and fun things 
                for families to do together. Vote on your favourites and let me know what you think! 💜
              </p>
            </div>
          </div>
        </>
      )}

      {/* Preview Header (for embedded view) */}
      {isPreview && (
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-400 via-purple-400 to-orange-300 p-1">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  <img 
                    src={NIAMH_AVATAR} 
                    alt="Niamh - Play CEO" 
                    className="w-16 h-16 object-cover rounded-full"
                  />
                </div>
              </div>
              <div className="absolute -bottom-1 -right-1 bg-yellow-400 rounded-full px-2 py-0.5 text-xs font-bold text-purple-900 shadow-lg">
                CEO
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Niamh&apos;s Corner
              </h2>
              <p className="text-purple-600 font-medium">Play CEO</p>
              <p className="text-sm text-gray-600">Play • Create • Explore • Smile</p>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter - only on full page */}
      {!isPreview && (
        <div className="max-w-5xl mx-auto px-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === null
                  ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md"
                  : "bg-white text-gray-700 hover:bg-purple-100 border border-purple-100"
              }`}
            >
              All Posts
            </button>
            {Object.entries(CATEGORY_BADGES).map(([key, { label, emoji }]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === key
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md"
                    : "bg-white text-gray-700 hover:bg-purple-100 border border-purple-100"
                }`}
              >
                {emoji} {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Posts Grid */}
      <div className={`${isPreview ? "" : "max-w-5xl mx-auto px-4"} grid gap-6 ${isPreview ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {displayPosts.map((post) => {
          const badge = CATEGORY_BADGES[post.category] || CATEGORY_BADGES.activity;
          const postComments = comments[post.post_id] || [];
          const isCommentsExpanded = expandedComments[post.post_id];
          
          return (
            <div
              key={post.post_id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-purple-100 hover:border-purple-300 transition-all hover:shadow-xl"
            >
              {/* Post Image */}
              {post.image_url && (
                <div className="w-full h-48 overflow-hidden">
                  <img 
                    src={post.image_url} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {/* Category Badge */}
              <div className="px-4 pt-4">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                  {badge.emoji} {badge.label}
                </span>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {post.content}
                </p>

                {/* Media Links */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.video_url && (
                    <a
                      href={post.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      <Play size={14} /> Watch Video
                    </a>
                  )}
                  {post.activity_sheet_url && (
                    <a
                      href={post.activity_sheet_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                    >
                      <FileText size={14} /> Activity Sheet
                    </a>
                  )}
                </div>

                {/* Poll (if exists) */}
                {post.poll && (
                  <div className="bg-purple-50 rounded-xl p-4 mb-4">
                    <p className="font-medium text-purple-900 mb-3">{post.poll.question}</p>
                    <div className="space-y-2">
                      {post.poll.options.map((option, idx) => {
                        const votes = post.poll.results?.[idx] || 0;
                        const totalVotes = Object.values(post.poll.results || {}).reduce((a, b) => a + b, 0);
                        const percentage = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => handlePollVote(post.poll.poll_id, idx)}
                            className="w-full text-left p-2 rounded-lg bg-white border-2 border-purple-200 hover:border-purple-400 transition-colors relative overflow-hidden"
                          >
                            <div
                              className="absolute inset-0 bg-purple-100 transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                            <span className="relative flex justify-between">
                              <span>{option}</span>
                              <span className="text-purple-600 font-medium">{percentage}%</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Vote & Comment Buttons */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleVote(post.post_id)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium hover:bg-pink-200 transition-colors"
                    >
                      <Heart size={16} className="fill-current" />
                      {post.total_votes} Love{post.total_votes !== 1 ? "s" : ""}
                    </button>
                    {(post.comments_enabled || post.prompt_question) && !isPreview && (
                      <button
                        onClick={() => toggleComments(post.post_id)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium hover:bg-emerald-200 transition-colors"
                      >
                        <MessageCircle size={16} />
                        {post.comments_count || 0}
                      </button>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(post.created_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Interactive Prompt Question */}
                {post.prompt_question && !isPreview && (
                  <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                    <p className="text-purple-800 font-medium text-sm italic">
                      💬 {post.prompt_question}
                    </p>
                  </div>
                )}

                {/* Comments Section */}
                {isCommentsExpanded && !isPreview && (
                  <div className="mt-4 border-t border-purple-100 pt-4">
                    {/* Comment Input */}
                    <div className="flex gap-2 mb-4">
                      <input
                        type="text"
                        value={newComment[post.post_id] || ""}
                        onChange={(e) => setNewComment(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                        placeholder="Share your thoughts..."
                        className="flex-1 px-4 py-2 border border-purple-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                        onKeyPress={(e) => e.key === "Enter" && submitComment(post.post_id)}
                      />
                      <button
                        onClick={() => submitComment(post.post_id)}
                        disabled={submittingComment[post.post_id]}
                        className="px-4 py-2 bg-purple-500 text-white rounded-full text-sm font-medium hover:bg-purple-600 transition-colors disabled:opacity-50"
                      >
                        {submittingComment[post.post_id] ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Send size={16} />
                        )}
                      </button>
                    </div>
                    
                    {/* Comments List */}
                    {postComments.length > 0 ? (
                      <div className="space-y-3">
                        {postComments.map((comment) => (
                          <div key={comment.comment_id} className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-bold">
                                {comment.author_name?.charAt(0).toUpperCase() || "?"}
                              </div>
                              <span className="text-sm font-medium text-gray-800">{comment.author_name}</span>
                              <span className="text-xs text-gray-400">
                                {new Date(comment.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 pl-8">{comment.content}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 text-sm py-2">No comments yet. Be the first!</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* View All Link (for preview) */}
      {isPreview && posts.length > 0 && (
        <div className="text-center mt-6">
          <Link
            to="/niamhs-corner"
            className="inline-flex items-center gap-2 text-purple-600 font-medium hover:text-purple-800 transition-colors"
          >
            See all of Niamh&apos;s posts <ChevronRight size={16} />
          </Link>
        </div>
      )}

      {/* Empty State */}
      {!loading && posts.length === 0 && (
        <div className="text-center py-12">
          <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-4" />
          <p className="text-gray-500">No posts yet! Check back soon for Niamh&apos;s latest adventures!</p>
        </div>
      )}

      {/* Bottom Navigation - only on full page */}
      {!isPreview && <BottomNav />}
    </div>
  );
};

export default NiamhsCorner;
